import { useFormikContext } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { Grid } from 'src/components/Grid';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { AssessmentField } from 'src/models/assessment/_types';

import { AssessmentFormValues } from '../AssessmentNewPage';

import { AddMeasurementDialog } from './AddMeasurementDialog';
import { AssessmentQuestionField } from './AssessmentQuestionField';

interface FieldOption {
  id: string;
  name: string;
  label: string;
}

export const AssessmentFields = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessmentFields',
  });
  const { t: tGlobal } = useTranslation();
  const { t: tField } = useTranslation('translation', {
    keyPrefix: 'assessmentQuestionField',
  });
  const { values, setFieldValue } = useFormikContext<AssessmentFormValues>();
  const { session, sessionTrainer } = useSession();

  const [showConditional, setShowConditional] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<number>(-1);
  const [selectedFieldId, setSelectedFieldId] = useState<string>('');
  const [openAddMeasurementDialog, setOpenAddMeasurementDialog] =
    useState(false);
  // Helper function to get translated field name
  const getFieldName = (id: string, label: string): string => {
    if (label === 'system') {
      // System fields use their id directly
      return id.charAt(0).toUpperCase() + id.slice(1).replace(/_/g, ' ');
    }
    if (label.length > 0 && label !== 'system') {
      // Profile fields use their label
      return label;
    }

    // Regular fields use translated names
    switch (id) {
      case 'text':
        return tField('shortAnswer');
      case 'textarea':
        return tField('longAnswer');
      case 'checkbox':
        return tField('checkboxGroup');
      case 'dropdown':
        return tField('dropdown');
      case 'header':
        return tField('sectionHeader');
      case 'instructions':
        return tField('instructionsDetails');
      case 'formula':
        return tField('formulaField');
      case 'password':
        return tField('password');
      case 'upload':
        return tGlobal('trainer.progress.full');
      case 'file':
        return tField('fileUpload');
      case 'credit_card':
        return tField('paymentMethod');
      case 'waiver':
        return tField('waiver');
      case 'toggle':
        return tField('toggle');
      case 'rep_max':
        return `${tField('repMax')}`.trim();
      default:
        return id;
    }
  };

  const defaultFields: FieldOption[] = [
    { id: 'text', name: '', label: '' },
    { id: 'textarea', name: '', label: '' },
    { id: 'checkbox', name: '', label: '' },
    { id: 'dropdown', name: '', label: '' },
    { id: 'header', name: '', label: '' },
    { id: 'instructions', name: '', label: '' },
    { id: 'formula', name: '', label: '' },
    { id: 'password', name: '', label: '' },
    { id: 'upload', name: '', label: '' },
    { id: 'file', name: '', label: '' },
    { id: 'credit_card', name: '', label: '' },
    { id: 'dob', name: '', label: 'Date of Birth' },
    { id: 'height', name: '', label: 'Height (inches)' },
    { id: 'gender', name: '', label: 'Gender' },
    { id: 'profile_image', name: '', label: 'Profile Image' },
    { id: 'first_name', name: '', label: 'First Name' },
    { id: 'last_name', name: '', label: 'Last Name' },
    { id: 'phone_number', name: '', label: 'Phone Number' },
    { id: 'street_address', name: '', label: 'Street Address' },
    { id: 'location_id', name: '', label: 'Location' },
    { id: 'city', name: '', label: 'City' },
    { id: 'state', name: '', label: 'State' },
    { id: 'zip_code', name: '', label: 'Zip Code' },
    { id: 'email', name: '', label: 'Email' },
    { id: 'weight', name: 'Weight', label: 'system' },
    { id: 'bmi', name: 'BMI', label: 'system' },
    { id: 'systolic_bp', name: 'Systolic BP', label: 'system' },
    { id: 'diastolic_bp', name: 'Diastolic BP', label: 'system' },
    { id: 'body_fat_', name: 'Body Fat %', label: 'system' },
    { id: 'rhr', name: 'Resting Heart Rate', label: 'system' },
    { id: 'waist', name: 'Waist Size', label: 'system' },
    { id: 'thigh', name: 'Thigh Size', label: 'system' },
    { id: 'bicep', name: 'Bicep Size', label: 'system' },
    { id: 'waiver', name: '', label: '' },
    { id: 'toggle', name: '', label: '' },
    { id: 'rep_max', name: '', label: '' },
  ];

  // Sort fields by index
  const sortedFields = values.fields.sort(
    (a, b) => (a.index || 0) - (b.index || 0)
  );

  const measurementText = tGlobal('measurement.one').toLowerCase();
  const profileFieldText = t('profileField');
  const progressPhotoText = t('progressPhoto');

  // Get available field options
  const fieldOptions: FieldOption[] = (() => {
    let fields = defaultFields.map((field) => {
      let name = getFieldName(field.id, field.label);

      // Match Ember logic: if field.label == 'system', add measurement suffix
      if (field.label === 'system') {
        name += ` - (${measurementText})`;
      }
      // Match Ember logic: if field.label.length > 0, add profile field suffix
      // Note: This applies to profile fields, but also other fields with labels
      // In Ember: if (field.label.length > 0) name += ' - (profile field)';
      // This means all fields with a label (except system which is handled above) get profile field suffix
      if (field.label.length > 0 && field.label !== 'system') {
        name += ` - (${profileFieldText})`;
      }
      // Match Ember logic: if field.name == 'Photo/Video', add progress photo suffix
      // In Ember defaultFields, upload field has name: 'Photo/Video', id: 'upload'
      // So we check by id === 'upload'
      if (field.id === 'upload') {
        name += ` - (${progressPhotoText})`;
      }

      return {
        id: field.id,
        name,
        label: field.label,
      };
    });

    // Add healthInfoMetrics (statsList) - matches Ember: currentWhiteLabeler.healthInfoMetrics
    if (session?.statsList && session.statsList.length > 0) {
      const trainerMetrics = session.statsList.map((metric) => ({
        id: metric.stat,
        name: `${metric.label} - (${measurementText})`,
        label: 'system',
      }));
      fields = fields.concat(trainerMetrics);
    }

    // Add trainerProfileFields - matches Ember: currentWhiteLabeler.trainerProfileFields
    if (
      sessionTrainer?.trainerProfileFields &&
      sessionTrainer.trainerProfileFields.length > 0
    ) {
      const profileFields = sessionTrainer.trainerProfileFields.map(
        (field: string) => ({
          id: field,
          name: `${field} - (${profileFieldText})`,
          label: 'profile',
        })
      );
      fields = fields.concat(profileFields);
    }

    // Add trainingInfoFields - matches Ember: currentPlatform.trainingInfoFields
    if (session?.trainingInfoFields && session.trainingInfoFields.length > 0) {
      const trainerFields = session.trainingInfoFields.map((field) => ({
        id: field.id,
        name: field.label,
        label: 'training',
      }));
      fields = fields.concat(trainerFields);
    }

    return fields;
  })();

  // Position options
  const positionOptions = (() => {
    const items: { id: number; name: string }[] = [];
    const length = sortedFields.length;

    for (let i = 0; i <= length + 1; i++) {
      const name =
        i > length ? t('atTheEnd') : t('beforeQuestion', { number: i + 1 });
      const id = i > length ? -1 : i;
      items.push({ id, name });
    }

    return items;
  })();

  // Check if all fields are required
  const allRequired =
    sortedFields.length > 0 &&
    sortedFields.every((field) => field.required === true);

  const toggleAllRequired = () => {
    const newValue = !allRequired;
    const updatedFields = sortedFields.map((field) => ({
      ...field,
      required: newValue,
    }));
    setFieldValue('fields', updatedFields);
  };

  const addField = (fieldId: string) => {
    if (!fieldId) return;

    const field = fieldOptions.find((f) => f.id === fieldId);
    if (!field) return;

    const position =
      selectedPosition >= 0 ? selectedPosition : sortedFields.length;
    let newField: AssessmentField;

    if (field.label === 'system') {
      newField = {
        type: 'system',
        stat: field.id,
        value: null,
        index: position,
        label: '',
      } as AssessmentField;
    } else if (field.label === 'training') {
      newField = {
        type: 'training',
        stat: field.id,
        value: null,
        index: position,
        label: '',
      } as AssessmentField;
    } else if (field.label === 'profile') {
      // Profile fields are treated as regular fields with the field id as the type
      newField = {
        type: field.id as any,
        index: position,
        label: '',
      } as AssessmentField;
    } else if (
      field.id === 'dropdown' ||
      field.id === 'checkbox' ||
      field.id === 'toggle'
    ) {
      const options =
        field.id === 'toggle'
          ? [
              { label: '', default: false, points: 0 },
              { label: '', default: false, points: 0 },
            ]
          : [{ label: '', default: false, points: 0 }];
      newField = {
        type: field.id as 'dropdown' | 'checkbox' | 'toggle',
        options,
        index: position,
        label: '',
      } as AssessmentField;
    } else {
      newField = {
        type: field.id as any,
        index: position,
        label: '',
      } as AssessmentField;
    }

    const updatedFields = [...sortedFields];
    updatedFields.splice(position, 0, newField);

    // Update indices
    updatedFields.forEach((f, i) => {
      f.index = i;
    });

    setFieldValue('fields', updatedFields);
    setSelectedPosition(-1);
  };

  const deleteField = (index: number) => {
    const updatedFields = sortedFields.filter((_, i) => i !== index);
    updatedFields.forEach((f, i) => {
      f.index = i;
    });
    setFieldValue('fields', updatedFields);
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sortedFields.length) return;

    const updatedFields = [...sortedFields];
    const [removed] = updatedFields.splice(index, 1);
    updatedFields.splice(newIndex, 0, removed);
    updatedFields.forEach((f, i) => {
      f.index = i;
    });
    setFieldValue('fields', updatedFields);
  };

  return (
    <Stack spacing={3}>
      {/* Header controls */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box onClick={() => setShowConditional(!showConditional)}>
            <Typography
              variant="body2"
              sx={{ cursor: 'pointer', color: 'primary.main' }}
            >
              {showConditional ? t('hide') : t('show')}{' '}
              {t('conditionalOptions')}
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Tooltip
              title={tGlobal('assessmentFields.allFieldsRequiredTooltip', {
                assessment: tGlobal('assessment.one').toLowerCase(),
              })}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  {t('allFieldsRequired')}
                </Typography>
                <Checkbox
                  checked={allRequired}
                  onChange={toggleAllRequired}
                  size="small"
                />
              </Box>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>

      {/* Fields list */}
      <Stack spacing={2}>
        {sortedFields.map((field, index) => (
          <AssessmentQuestionField
            key={index}
            field={field}
            index={index}
            numFields={sortedFields.length}
            showConditional={showConditional}
            onDelete={deleteField}
            onMoveUp={(idx) => moveField(idx, 'up')}
            onMoveDown={(idx) => moveField(idx, 'down')}
          />
        ))}
      </Stack>

      {/* Add new field */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Box>
            <TextField
              label={t('addNewField')}
              value={selectedFieldId}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedFieldId(value);
                if (value) {
                  addField(value);
                  setSelectedFieldId(''); // Reset after adding
                }
              }}
              options={fieldOptions.map((f) => ({
                value: f.id,
                label: f.name,
              }))}
              fullWidth
            />
            <Button
              variant="text"
              onClick={() => {
                setOpenAddMeasurementDialog(true);
              }}
              sx={{ mt: 1 }}
            >
              {tGlobal('measurements.addCustomMeasurement', {
                measurement: tGlobal('measurement.one'),
              })}
            </Button>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label={t('selectFieldPosition')}
            value={selectedPosition >= 0 ? selectedPosition : ''}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedPosition(value === '' ? -1 : Number(value));
            }}
            options={positionOptions.map((o) => ({
              value: o.id,
              label: o.name,
            }))}
            fullWidth
          />
        </Grid>
      </Grid>
      {openAddMeasurementDialog && (
        <AddMeasurementDialog
          open={true}
          onClose={() => setOpenAddMeasurementDialog(false)}
        />
      )}
    </Stack>
  );
};
